import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";

/**
 * Per-user exercise-favourite set. Wired up at the App root (inside the
 * authenticated tree) so any component in the in-app surface can read or
 * toggle a star with a single hook call.
 *
 * Backend:
 *   - GET /api/exercises/favourites -> string[]
 *   - POST /api/exercises/favourite {name} -> {favourited: bool, favourites: string[]}
 */
const FavouritesContext = createContext({
  favourites: new Set(),
  loaded: false,
  isFavourited: () => false,
  toggle: async () => null,
  refresh: async () => null,
});

export const FavouritesProvider = ({ children }) => {
  const { user } = useAuth();
  const [favourites, setFavourites] = useState(new Set());
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    if (!user) {
      setFavourites(new Set());
      setLoaded(true);
      return;
    }
    try {
      const { data } = await api.get("/exercises/favourites");
      setFavourites(new Set(data || []));
    } catch {
      // soft-fail — leave whatever we had
    } finally {
      setLoaded(true);
    }
  }, [user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const isFavourited = useCallback(
    (name) => favourites.has(name),
    [favourites]
  );

  const toggle = useCallback(async (name) => {
    try {
      const { data } = await api.post("/exercises/favourite", { name });
      setFavourites(new Set(data.favourites || []));
      return data.favourited;
    } catch {
      return null;
    }
  }, []);

  return (
    <FavouritesContext.Provider value={{ favourites, loaded, isFavourited, toggle, refresh }}>
      {children}
    </FavouritesContext.Provider>
  );
};

export const useFavourites = () => useContext(FavouritesContext);
